import { forwardRef, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import LordIcon from 'components/global/LordIcon';

import useMergedRef from 'hooks/useMergedRef';

import { THEME } from 'utils/Theme';

import { faTimes } from '@fortawesome/pro-regular-svg-icons';

import Button from './Button';
import StringInput from './StringInput';
import * as S from './styled/SearchInput.styled';

/**
 * @author Burak Unsal
 * @description Integer input component
 * @param {object} props
 * @param {String} [props.variant = 'outline'] - variant of the search component field.
 * @param {Boolean} [props.fullWidth = false] - fullWidth state of the search component field.
 * @param {Boolean} [props.expandable = false] - expandable state of the search component field.
 * @param {Boolean} [props.expandableIcon = false] - expandableIcon state of the search component field.
 * @param {String} [props.placeholder] - placeholder of the input field.
 * @param {String} [props.id] - id of the input field.
 * @param {String} [props.name] - name of the input field.
 * @param {String} [props.type] - type of the input field.
 * @param {String} [props.maxlength = 255] - maxlength of the input field.
 * @param {String} [props.errorMessage] - error message of the input field.
 * @param {Boolean} [props.viewMode = false] - viewMode state of the input field.
 * @param {Boolean} [props.disabled] - disabled state of the input field.
 * @param {String} [props.className] - class name of the input field.
 * @param {String} [props.value] - value of the global component.
 * @param {Function} [props.setValue] - setValue function for global component.
 */
const SearchInput = (props, ref) => {
	const {
		variant = 'outline',
		fullWidth = false,
		expandable = false,
		expandableIcon = false,
		placeholder = 'search',
		...restProps
	} = props;
	const [componentRef, setComponentRef] = useState(null);
	const refs = useMergedRef(ref, setComponentRef);
	const targetRef = useRef(null);

	const handleRemoveInput = () => {
		const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
		nativeInputValueSetter.call(componentRef, '');

		const inputEvent = new Event('input', { bubbles: true });
		componentRef.dispatchEvent(inputEvent);
	};

	const darkMode = useSelector((state) => state.layout.darkMode);

	return (
		<S.InputContainer
			ref={targetRef}
			className={`${variant} ${fullWidth ? 'full-width' : ''} ${expandable ? 'expandable' : ''} ${expandableIcon ? 'expandable-icon' : ''} search-input`}>
			<S.LordIconContainer>
				<LordIcon
					primaryColor={darkMode ? THEME.WHITE : THEME.BLACK}
					secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
					src="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/search-icon.json.gz"
					target={targetRef}
					width={24}
					height={24}
				/>
			</S.LordIconContainer>

			<StringInput
				ref={refs}
				placeholder={placeholder}
				onKeyDown={(e) => {
					// blur on enter
					if (e.keyCode === 13) {
						e.target.blur();
					}
				}}
				{...restProps}
			/>
			{componentRef?.value && (
				<Button
					variant="borderless"
					iconButton
					size="small"
					iconSize="small"
					icon={faTimes}
					className="delete-button"
					onClick={handleRemoveInput}
				/>
			)}
		</S.InputContainer>
	);
};

/**
 * @author Burak Unsal
 * @description Integer input component
 * @param {Function} SearchInput
 * @param {Object} SearchInput.props - props of the component.
 * @param {String} [props.variant = 'outline'] - variant of the search component field.
 * @param {Boolean} [props.fullWidth = false] - fullWidth state of the search component field.
 * @param {Boolean} [props.expandable = false] - expandable state of the search component field.
 * @param {String} [props.placeholder] - placeholder of the input field.
 * @param {String} [props.id] - id of the input field.
 * @param {String} [props.name] - name of the input field.
 * @param {String} [props.type] - type of the input field.
 * @param {String} [props.maxlength = 255] - maxlength of the input field.
 * @param {String} [props.errorMessage] - error message of the input field.
 * @param {Boolean} [props.viewMode = false] - viewMode state of the input field.
 * @param {Boolean} [props.disabled] - disabled state of the input field.
 * @param {String} [props.className] - class name of the input field.
 * @param {String} [props.value] - value of the global component.
 * @param {Function} [props.setValue] - setValue function for global component.
 */
export default forwardRef(SearchInput);
